import { Input, Modal, Form } from "antd";
import React, { Fragment, useState, useEffect, useCallback } from "react";
import styled from "@emotion/styled";
import { getMapApi, parseAddressComponents } from "../../utils/googleMap";
import { AddressEditMap } from "./addressEditMap";
import { PlacesAutoComplete } from "./placesAutocomplete";
import { Address } from "src/interface";
import { showErrorToast } from "@src/appV2/lib/Notifications";

const AddressModal = styled(Modal)`
  top: 0px !important;
  padding-bottom: 0 !important;
  height: calc(100% - 16px) !important;

  .ant-modal-content {
    height: 100%;
  }

  .ant-modal-body {
    height: calc(100% - 55px);
    padding: 0;
  }
  .map-container {
    height: calc(100% - 180px);
  }
  form {
    padding: 20px;
    .ant-row {
      margin-bottom: 0;
    }
  }
`;

type AddressEditModalProps = {
  visible: boolean | undefined;
  onComplete: (address: unknown) => Promise<unknown>;
  onCancel: () => void;
};

function AddressEditModal({ visible, onComplete, onCancel }: AddressEditModalProps) {
  const [isMap, setIsMap] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [address, setAddress] = useState<Address>({});
  const [location, setLocation] = useState({});
  const [refresh, setRefresh] = useState(1);

  useEffect(() => {
    setIsMap(false);
    setRefresh(refresh + 1);
    setAddress({});
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const onPlaceChange = useCallback((place) => {
    if (!place) {
      return;
    }
    setLocation(place.location);
    setAddress(place.address);
    setIsMap(true);
  }, []);

  const onLocationChange = async (markerLocation) => {
    setLoading(true);
    setLocation(markerLocation);
    void updateAddress(markerLocation);
  };

  const updateAddress = async (markerLocation) => {
    const mapApi = await getMapApi();
    const geocoder = new mapApi.Geocoder();
    geocoder.geocode({ location: markerLocation }, (results, status) => {
      if (status !== "OK") {
        showErrorToast("Invalid address");
        return;
      }

      const [place] = results;

      const geocodeAddress = parseAddressComponents(place.address_components);

      setAddress({
        ...address,
        ...geocodeAddress,
        formatted: place.formatted_address,
        subpremise: "",
      });

      setLoading(false);
    });
  };

  const onLineChange = (event) => {
    setAddress({
      ...address,
      line1: event.target.value,
    });
  };

  const onOk = async () => {
    setSaving(true);
    try {
      await onComplete({
        location,
        address: {
          ...address,
          line1: address.line1 || "",
          subpremise: address.subpremise || "",
        },
      });
    } finally {
      setSaving(false);
    }
  };

  const onLocationFocus = () => setIsMap(false);

  return (
    <AddressModal
      title={null}
      closable={false}
      okText={isMap ? "Update Address" : "Next"}
      okButtonProps={{ disabled: !isMap || saving, loading: saving }}
      onOk={onOk}
      onCancel={onCancel}
      visible={visible}
    >
      {isMap ? (
        <Fragment>
          <div className="map-container">
            <AddressEditMap location={location} onChange={onLocationChange} />
          </div>
          <Form layout="vertical">
            <Form.Item label="Address">
              <Input
                value={loading ? "Fetching address" : address.formatted}
                onFocus={onLocationFocus}
              />
            </Form.Item>
            <Form.Item label="Apartment/ Floor/ Building">
              <Input
                placeholder="Apartment, e.g. “Unit 2”"
                onChange={onLineChange}
                value={address.subpremise || address.line1 || ""}
                disabled={Boolean(address.subpremise)}
              />
            </Form.Item>
          </Form>
        </Fragment>
      ) : (
        <Form layout="vertical">
          <Form.Item label="Search Location">
            <PlacesAutoComplete refresh={refresh} onChange={onPlaceChange} />
          </Form.Item>
        </Form>
      )}
    </AddressModal>
  );
}

export { AddressEditModal };
